import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, HelpCircle } from "lucide-react";
import Navbar from "../components/Navbar";
import Disclaimer from "../components/Disclaimer";
import Wordmark from "../components/Wordmark";
import { useAuth } from "../context/AuthContext";

const FAQS = [
  {
    q: "Does The Dial log into my Netflix, Hulu, or other accounts?",
    a: "No \u2014 never. The Dial doesn't take or store your streaming passwords. You just tell it which services you pay for, and it uses that to show you where movies and shows are streaming.",
  },
  {
    q: "Do I watch movies and shows on The Dial?",
    a: "Nope. The Dial is the guide, not the channel. It tells you where something is streaming, then sends you to the right app to watch it.",
  },
  {
    q: "Is The Dial free?",
    a: "The core guide is free. Full Access unlocks every era skin and feature \u2014 use code GETINTOIT for Full Access.",
  },
  {
    q: "What are the era skins?",
    a: "The Dial dresses itself up like the TV eras you grew up with. Pick the look you want \u2014 the guide works the same underneath.",
  },
  {
    q: "What is Indie Spotlight?",
    a: "A section for independent films, sitting right next to the major titles in genre browse. Filmmakers keep 100% ownership of their work \u2014 The Dial just links out to wherever they host it.",
  },
  {
    q: "What does adding friends do?",
    a: "Unlocks the \u201cLoved by Friends\u201d row \u2014 a feed of what your friends are favoriting, so you always have something to watch.",
  },
  {
    q: "Does The Dial track me or sell my data?",
    a: "No. Your watchlist is yours. Analytics are aggregate-only \u2014 we look at trends, never at you.",
  },
  {
    q: "How do I submit my film to Indie Spotlight?",
    a: "Hit \u201cSubmit Your Film\u201d in the Indie Spotlight section and fill out the form. Once your film is approved, it goes live with an Indie badge \u2014 and you can open a discussion about your film right on its page, where users can ask questions and talk about it with you.",
  },
];

export default function FAQ() {
  const { user } = useAuth();
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (i) => setOpenIndex((cur) => (cur === i ? null : i));

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      {user ? (
        <Navbar />
      ) : (
        <header className="sticky top-0 z-50 w-full border-b border-[var(--border-subtle)] bg-[var(--bg-surface)]">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 sm:px-8">
            <Link to="/" className="inline-flex items-center" data-testid="faq-brand-logo">
              <Wordmark sizeClass="text-2xl sm:text-3xl" />
            </Link>
            <div className="flex items-center gap-4 text-sm text-[var(--text-secondary)]">
              <Link to="/login" className="hover:text-[var(--text-primary)]" data-testid="faq-signin-link">Sign in</Link>
              <Link to="/register" className="hover:text-[var(--text-primary)]" data-testid="faq-register-link">Create account</Link>
            </div>
          </div>
        </header>
      )}

      <main className="mx-auto max-w-3xl px-4 pb-20 pt-8 sm:px-6 lg:px-8" data-testid="faq-page">
        <div className="flex items-center gap-3" data-testid="faq-header">
          <HelpCircle size={32} className="flex-none text-[var(--accent)]" />
          <div>
            <h1 className="font-head text-4xl tracking-wide text-[var(--text-primary)] sm:text-5xl">
              Frequently Asked Questions
            </h1>
            <p className="mt-1 text-[var(--text-secondary)]">Everything you need to know about The Dial.</p>
          </div>
        </div>

        <div className="mt-8 space-y-3" data-testid="faq-list">
          {FAQS.map((item, i) => {
            const isOpen = openIndex === i;
            const panelId = `faq-panel-${i}`;
            const btnId = `faq-button-${i}`;
            return (
              <div
                key={i}
                className="overflow-hidden rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-surface)]"
                data-testid={`faq-item-${i}`}
              >
                <h2>
                  <button
                    id={btnId}
                    type="button"
                    onClick={() => toggle(i)}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-[var(--accent)]/10"
                    data-testid={`faq-question-${i}`}
                  >
                    <span className="text-base font-semibold text-[var(--text-primary)]">{item.q}</span>
                    <ChevronDown
                      size={20}
                      className={`flex-none text-[var(--accent)] transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                    />
                  </button>
                </h2>
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={btnId}
                  hidden={!isOpen}
                  className="border-t border-[var(--border-subtle)] px-5 py-4"
                  data-testid={`faq-answer-${i}`}
                >
                  <p className="text-sm leading-relaxed text-[var(--text-secondary)] sm:text-base">{item.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      <Disclaimer />
    </div>
  );
}
